import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";

import { getConfigWithToken } from "../config/config";
import { getSuperAdminActivityLogById } from "../config/url";
import classes from "./SuperAdminList.module.css";

const formatDate = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";
	return date.toLocaleString("en-US", {
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
		month: "short",
		year: "numeric",
	});
};

const SuperAdminActivityLogDetails = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const token = useSelector((state) => state?.user?.user?.token) || localStorage.getItem("user_id");
	const [activityLog, setActivityLog] = useState(null);
	const [hasError, setHasError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
			},
		}),
		[token],
	);

	const fetchActivityLog = useCallback(async () => {
		setIsLoading(true);
		setHasError(false);
		try {
			const response = await axios.get(getSuperAdminActivityLogById(id), requestConfig);
			setActivityLog(response?.data?.data || null);
		} catch {
			setHasError(true);
			setActivityLog(null);
		} finally {
			setIsLoading(false);
		}
	}, [id, requestConfig]);

	useEffect(() => {
		fetchActivityLog();
	}, [fetchActivityLog]);

	return (
		<div className={classes.page}>
			<button className={classes.backButton} onClick={() => navigate("/activity-logs")} type="button">
				<ArrowLeft size={16} />
				Back to Activity Logs
			</button>
			<h1 className={classes.title}>Activity Log Details</h1>

			{isLoading ? (
				<p className={classes.subtitle}>Loading...</p>
			) : hasError || !activityLog ? (
				<div className={classes.errorBox}>
					<p>Activity log could not be loaded.</p>
					<button className={classes.retryButton} onClick={fetchActivityLog} type="button">
						Retry
					</button>
				</div>
			) : (
				<div className={classes.detailCard}>
					<div>
						<p className={classes.detailLabel}>Action</p>
						<p className={classes.detailValue}>{activityLog.action}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>User</p>
						<p className={classes.detailValue}>{activityLog.user_name || "-"}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Entity Type</p>
						<p className={classes.detailValue}>{activityLog.entity_type || "-"}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Entity ID</p>
						<p className={classes.detailValue}>{activityLog.entity_id || "-"}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Client ID</p>
						<p className={classes.detailValue}>{activityLog.clientid || "-"}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Status</p>
						<p className={classes.detailValue}>{activityLog.status || "-"}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Organization ID</p>
						<p className={classes.detailValue}>{activityLog.organization_id || "-"}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Created</p>
						<p className={classes.detailValue}>{formatDate(activityLog.created_at)}</p>
					</div>
					<div>
						<p className={classes.detailLabel}>Metadata</p>
						<p className={classes.detailValue}>
							{activityLog.metadata ? JSON.stringify(activityLog.metadata) : "-"}
						</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default SuperAdminActivityLogDetails;
